import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, User, MessageSquare, Mic, ChevronRight } from 'lucide-react'
import { useSearch, type SearchResult } from '@/hooks/use-search'
import { useDebouncedValue } from '@/hooks/use-debounced-value'

const typeIcons: Record<string, typeof User> = {
  contact: User,
  interaction: MessageSquare,
  voice_note: Mic,
}

function resultLink(result: SearchResult): string {
  switch (result.type) {
    case 'contact':
      return `/contacts/${result.id}`
    case 'interaction':
      return `/contacts/${result.id}`
    case 'voice_note':
      return `/contacts/${result.id}`
    default:
      return '/'
  }
}

export default function SearchPage() {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 300)
  const { data, isLoading } = useSearch(debouncedQuery)

  const allResults = [
    ...(data?.contacts ?? []),
    ...(data?.interactions ?? []),
    ...(data?.voice_notes ?? []),
  ]

  return (
    <div className="flex flex-1 flex-col px-6 pt-6 pb-8">
      <h1 className="font-heading mb-4 text-2xl font-bold">Search</h1>

      <div className="relative mb-6">
        <Search className="text-on-surface-variant absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search contacts, interactions, voice notes..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          className="border-outline-variant/30 bg-surface-container placeholder:text-on-surface-variant focus:ring-primary/30 focus:border-primary w-full rounded-xl border py-3 pr-4 pl-10 text-sm focus:ring-2 focus:outline-none"
        />
      </div>

      {isLoading && debouncedQuery.length >= 2 && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-surface-container-high h-14 animate-pulse rounded-xl" />
          ))}
        </div>
      )}

      {debouncedQuery.length >= 2 && !isLoading && allResults.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <Search className="text-on-surface-variant/50 mb-2 size-8" />
          <p className="text-on-surface-variant text-sm">No results found</p>
        </div>
      )}

      {query.length < 2 && (
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <Search className="text-on-surface-variant/50 mb-2 size-8" />
          <p className="text-on-surface-variant text-sm">Type at least 2 characters to search</p>
        </div>
      )}

      {data && (
        <div className="space-y-6">
          {data.contacts.length > 0 && (
            <section>
              <h2 className="text-on-surface-variant mb-3 text-xs font-semibold tracking-widest uppercase">
                Contacts
              </h2>
              <div className="bg-surface-container-low overflow-hidden rounded-xl">
                {data.contacts.map((result, i) => (
                  <ResultRow key={result.id} result={result} showBorder={i > 0} />
                ))}
              </div>
            </section>
          )}
          {data.interactions.length > 0 && (
            <section>
              <h2 className="text-on-surface-variant mb-3 text-xs font-semibold tracking-widest uppercase">
                Interactions
              </h2>
              <div className="bg-surface-container-low overflow-hidden rounded-xl">
                {data.interactions.map((result, i) => (
                  <ResultRow key={result.id} result={result} showBorder={i > 0} />
                ))}
              </div>
            </section>
          )}
          {data.voice_notes.length > 0 && (
            <section>
              <h2 className="text-on-surface-variant mb-3 text-xs font-semibold tracking-widest uppercase">
                Voice Notes
              </h2>
              <div className="bg-surface-container-low overflow-hidden rounded-xl">
                {data.voice_notes.map((result, i) => (
                  <ResultRow key={result.id} result={result} showBorder={i > 0} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function ResultRow({ result, showBorder }: { result: SearchResult; showBorder?: boolean }) {
  const Icon = typeIcons[result.type] ?? User
  return (
    <Link
      to={resultLink(result)}
      className={`hover:bg-surface-container-high flex items-center gap-4 p-4 transition-colors ${showBorder ? 'border-outline-variant/20 border-t' : ''}`}
    >
      <div className="bg-surface-container-highest flex size-10 shrink-0 items-center justify-center rounded-full">
        <Icon className="text-primary-dim size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{result.title}</p>
        {result.subtitle && (
          <p className="text-on-surface-variant truncate text-[10px]">{result.subtitle}</p>
        )}
        {result.highlight && (
          <p className="text-on-surface-variant mt-0.5 truncate text-xs italic">
            {result.highlight}
          </p>
        )}
      </div>
      <ChevronRight className="text-outline h-4 w-4" />
    </Link>
  )
}
